import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAllGamesSync } from "../data/allGames";
import Badge from "../components/Badge";
import Button from "../components/Button";
import { FaLink, FaChrome, FaAndroid, FaApple, FaDesktop } from "react-icons/fa";

const GOOGLE_PLAY_BADGE =
  "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";



const PLATFORM_ICON_MAP = {
  web: FaChrome,
  android: FaAndroid,
  ios: FaApple,
  mac: FaApple,
  pc: FaDesktop,
};


function stripHtml(input = "") {
  return String(input).replace(/<[^>]*>/g, "").trim();
}

function toMetaDescription(input = "", max = 155) {
  const text = stripHtml(input).replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}

function absoluteUrl(path) {
  const base = "https://kepithor.com";
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

function setOrCreateMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function upsertJsonLd(id, obj) {
  let script = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld", id);
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(obj);
}

function normalizeImageUrl(image) {
  if (!image) return null;
  if (String(image).startsWith("http")) return image;
  return absoluteUrl(image);
}


export default function GameDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ---- SSR payload (from game.php) ----
  const ssrId = typeof window !== "undefined" ? window.__SSR_GAME_ID__ : null;
  const ssrGame = typeof window !== "undefined" ? window.__SSR_GAME__ : null;

  const [hydrated, setHydrated] = useState(false);
  const [triedResolve, setTriedResolve] = useState(false);

  // Start with SSR game if present (prevents flicker)
  const [game, setGame] = useState(() => ssrGame || null);

  useEffect(() => {
    setHydrated(true);
  }, []);

  // Resolve game data
  useEffect(() => {
    // If we have SSR game and router param isn't ready yet, keep SSR
    if (ssrGame && !id) {
      setGame(ssrGame);
      setTriedResolve(true);
      return;
    }

    // If we have SSR game and the id matches, keep SSR (no lookup needed)
    if (ssrGame && ssrId && id && String(id) === String(ssrId)) {
      setGame(ssrGame);
      setTriedResolve(true);
      return;
    }

    // IMPORTANT: If SSR exists but id doesn't match, only then do SPA lookup
    const allGames = getAllGamesSync();
    const found = allGames.find((g) => String(g.id) === String(id)) || null;

    // If SSR game exists and lookup failed, DO NOT overwrite SSR with null.
    // This prevents "flash then not found" caused by id mismatch or missing local data.
    if (ssrGame && !found) {
      setGame(ssrGame);
      setTriedResolve(true);
      return;
    }

    setGame(found);
    setTriedResolve(true);
  }, [id, ssrGame, ssrId]);

  // SEO side effects
  useEffect(() => {
    if (!game) return;

    const canonical = absoluteUrl(`/game/${id || ssrId || ""}`);
    const { title, description, long_desc, image, genre, platforms = [], tags = [] } = game;


    const pageTitle = `${title} | Kepithor`;
    const metaDescription = toMetaDescription(
      description || `Read about ${title} on Kepithor.`,
      155
    );

    const ogImage = normalizeImageUrl(image);

    if (!ogImage) {
      document.head.querySelector('meta[property="og:image"]')?.remove();
      document.head.querySelector('meta[name="twitter:image"]')?.remove();
    }

    document.title = pageTitle;
    setCanonical(canonical);

    setOrCreateMeta('meta[name="description"]', { name: "description", content: metaDescription });

    setOrCreateMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setOrCreateMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Kepithor" });
    setOrCreateMeta('meta[property="og:title"]', { property: "og:title", content: pageTitle });
    setOrCreateMeta('meta[property="og:description"]', { property: "og:description", content: metaDescription });
    setOrCreateMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    if (ogImage) setOrCreateMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });

    setOrCreateMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: ogImage ? "summary_large_image" : "summary",
    });
    setOrCreateMeta('meta[name="twitter:title"]', { name: "twitter:title", content: pageTitle });
    setOrCreateMeta('meta[name="twitter:description"]', { name: "twitter:description", content: metaDescription });
    if (ogImage) setOrCreateMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });

    const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: title,
      description: stripHtml((long_desc && String(long_desc).trim()) ? long_desc : description),
      genre: genre || undefined,
      keywords: tags?.length ? tags.join(", ") : undefined,
      operatingSystem: platforms?.length ? platforms.join(", ") : "Web",
      applicationCategory: "Game",
      url: canonical,
      image: ogImage ? [ogImage] : undefined,
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
        { "@type": "ListItem", position: 2, name: title, item: canonical },
      ],
    };

    const clean = (o) =>
      JSON.parse(JSON.stringify(o, (_, v) => (v === undefined ? undefined : v)));

    upsertJsonLd("videogame", clean(videoGameSchema));
    upsertJsonLd("breadcrumb", clean(breadcrumbSchema));
  }, [id, ssrId, game]);

  // --- Render guards ---

// if (ssrGame && !game) {
//   return null;
// }


  if (!hydrated && ssrGame) return null;

  if (!hydrated && !ssrGame) {
    return (
      <div className="grid gap-4">
        <p className="text-slate-300">Loading…</p>
      </div>
    );
  }

  if (hydrated && triedResolve && !game) {
    return (
      <div className="grid gap-4">
        <p className="text-slate-300 mb-4">Game not found.</p>
        <Link to="/" className="text-emerald-300 underline text-sm hover:text-emerald-200">
          ← Back to directory
        </Link>
      </div>
    );
  }

  if (hydrated && !triedResolve) {
    return (
      <div className="grid gap-4">
        <p className="text-slate-300">Loading…</p>
      </div>
    );
  }

  // game is guaranteed now
  const {
  title,
  image,
  description,
  long_desc,
  genre,
  isWeb3,
  platforms = [],
  tags = [],
  source,
  webglPath,
  googlePlayUrl,
  websiteUrl,
} = game;


const displayDescription = (long_desc && String(long_desc).trim()) ? long_desc : (description || "");

  const displayImage = normalizeImageUrl(image);

const canPlayHere = !!webglPath;
const hasGooglePlay = !!(googlePlayUrl && String(googlePlayUrl).trim());
const hasWebsite = !!(websiteUrl && String(websiteUrl).trim());
  //const playUrl = canPlayHere ? null : (websiteUrl || "");



  return (

    <div className="grid gap-6">
    <br></br>
      <div className="flex items-center justify-between">
        <h1 className="text-title">{title}</h1>
        <Link
          to="/"
          className="px-3 py-2 rounded-xl border border-slate-700 text-button  text-slate-200 hover:border-emerald-400 hover:text-emerald-200 transition"
        >
          ← Back to directory
        </Link>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-1/3 rounded-2xl overflow-hidden border border-slate-800 bg-slate-900">
          {displayImage ? (
            <img src={displayImage} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-48 flex items-center justify-center text-slate-500 text-sm">
              No image available
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-3 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              {source ? <p className="text-meta mb-1">{source}</p> : null}

              <div className="flex items-center gap-3 flex-wrap">
                {isWeb3 && (
                  <span className="inline-flex items-center gap-1 text-emerald-300 text-body">
                    <FaLink />
                    <span>Web3 game</span>
                  </span>
                )}

                {platforms.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-200">
                    {platforms.map((p) => {
                      const Icon = PLATFORM_ICON_MAP[p] || FaDesktop;
                      return (
                        <span
                          key={p}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800 uppercase tracking-wide"
                        >
                          <Icon className="text-[13px]" />
                          {p}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <Badge>{genre}</Badge>
          </div>

<p className="text-body text-slate-200 whitespace-pre-line">
  {displayDescription}
</p>


          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {tags.map((tag) => (
                <span key={tag} className="px-2 py-1 rounded-full bg-slate-800 text-xs text-slate-100">
                  {tag}
                </span>
              ))}
            </div>
          )}

<div className="mt-4 flex flex-wrap gap-3 items-center">
  {canPlayHere && (
    <Button
      onClick={() => navigate(`/play/${id}`)}
      className="bg-emerald-500 text-white hover:bg-emerald-400 px-5 py-2.5 rounded-xl text-sm font-semibold"
    >
      Play Here
    </Button>
  )}

{hasGooglePlay && (
  <a
    href={googlePlayUrl}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Get it on Google Play"
    className="inline-flex items-center"
  >
    <img
      src={GOOGLE_PLAY_BADGE}
      alt="Get it on Google Play"
      className="h-12 w-auto"
      loading="lazy"
    />
  </a>
)}


  {hasWebsite && (
    <a
      href={websiteUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-slate-900/70 text-slate-100 px-5 py-2.5 rounded-xl border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition text-sm font-semibold inline-flex items-center justify-center"
    >
      Website
    </a>
  )}

  {!canPlayHere && !hasGooglePlay && !hasWebsite && (
    <span className="text-xs text-slate-400">
      This game isn&apos;t playable directly on this site.
    </span>
  )}

  <div className="spacer" style={{ height: 150 }} />
</div>


        </div>
      </div>
    </div>
  );
}
